/**
 * In-app browser detection.
 *
 * Instagram's and TikTok's embedded webviews are the known drop-off point: on
 * iOS they either deny getUserMedia outright or grant it and then break
 * MediaRecorder, and the native share sheet is unreliable. We would rather send
 * the guest to Safari or Chrome before they burn their one attempt.
 *
 * UA sniffing is unlovely but it is the only signal these webviews give us.
 */

export type InAppBrowser = 'instagram' | 'tiktok' | 'facebook' | 'snapchat' | 'other' | null;

export interface BrowserContext {
  inApp: InAppBrowser;
  isIOS: boolean;
  isAndroid: boolean;
  /** Whether we hold the guest at the interstitial. */
  shouldWarn: boolean;
}

export function detectBrowserContext(ua: string = navigator.userAgent): BrowserContext {
  const u = ua.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(u) || (/macintosh/.test(u) && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(u);

  let inApp: InAppBrowser = null;
  if (/instagram/.test(u)) inApp = 'instagram';
  else if (/tiktok|bytedance|musical_ly|bytelo/.test(u)) inApp = 'tiktok';
  else if (/\bfbav\b|\bfban\b|fb_iab|fbios/.test(u)) inApp = 'facebook';
  else if (/snapchat/.test(u)) inApp = 'snapchat';
  else if (isIOS && /\bwv\b/.test(u)) inApp = 'other';

  // Instagram and TikTok are the two we have copy for and the two the ads run
  // in. Facebook and Snapchat get the same treatment; they fail the same way.
  const shouldWarn = inApp !== null && inApp !== 'other';

  return { inApp, isIOS, isAndroid, shouldWarn };
}

/**
 * Best effort escape hatch. On Android an intent:// URL genuinely opens Chrome.
 * On iOS nothing reliably breaks out of the webview, so the interstitial has to
 * carry real instructions rather than lean on a button.
 */
export function openInSystemBrowser(): boolean {
  const { isAndroid } = detectBrowserContext();
  if (!isAndroid) return false;
  const url = window.location.href.replace(/^https?:\/\//, '');
  window.location.href = `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;
  return true;
}

export async function copyCurrentUrl(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(window.location.href);
    return true;
  } catch {
    return false;
  }
}
